import React, { useState } from 'react';
import { Card, CardBody, CardHeader, Collapse, Table } from 'reactstrap';

import Icon, { faChevronDown, faChevronUp, faCircleExclamation } from '~components/icon';
import { TableView } from '~components/table-view';
import { formatDateString } from '~libs/dayjs';

import { dexIssueColumns } from '../forms/dexPreviewColumns';
import { DexPreviewResult } from '../types';

interface ComponentProps {
  preview: DexPreviewResult;
  'data-cy'?: string;
}

const DexPreviewPanel: React.FC<ComponentProps> = ({ preview, 'data-cy': dataCy }) => {
  const [issuesOpen, setIssuesOpen] = useState(false);

  return (
    <div className="dex-preview-panel" data-cy={dataCy ?? 'dex-preview-panel'}>
      <Table bordered size="sm" style={{ maxWidth: '400px' }}>
        <tbody>
          <tr>
            <th scope="row">Date range</th>
            <td data-cy="dex-preview-date-range">
              {formatDateString(preview.date_from)} – {formatDateString(preview.date_to)}
            </td>
          </tr>
          <tr>
            <th scope="row">Clients</th>
            <td data-cy="dex-preview-client-count">{preview.client_count}</td>
          </tr>
          <tr>
            <th scope="row">Sessions</th>
            <td data-cy="dex-preview-session-count">{preview.session_count}</td>
          </tr>
        </tbody>
      </Table>

      {preview.issues.length > 0 && (
        <Card className="rounded-0 mt-2" data-cy="dex-preview-issues-card">
          <CardHeader
            className="d-flex justify-content-between border-0 fw-bold align-items-center"
            tag="button"
            type="button"
            onClick={() => setIssuesOpen((prev) => !prev)}
            aria-expanded={issuesOpen}
            aria-controls="dex-preview-issues-table"
            data-cy="dex-preview-issues-toggle"
          >
            <div>
              <Icon icon={faCircleExclamation} className="text-warning me-2" />
              Data quality issues ({preview.issues.length})
            </div>
            <Icon icon={issuesOpen ? faChevronUp : faChevronDown} className="ms-2" />
          </CardHeader>
          <Collapse isOpen={issuesOpen}>
            <CardBody className="p-0">
              <TableView
                id="dex-preview-issues-table"
                columns={dexIssueColumns}
                data={preview.issues.map((issue, index) => ({ ...issue, id: `${issue.appointment_id}-${index}` }))}
                emptyMessage="No issues"
                readonly
                striped
              />
            </CardBody>
          </Collapse>
        </Card>
      )}
    </div>
  );
};

export default DexPreviewPanel;
